import { apiClient, createFormDataRequest } from './client';
import type { ApiResponse, PaginatedResponse, PaginationParams, SortParams } from '../types/api';
import type {
  Trade,
  TradeImage,
  TradeImageType,
  CreateTradeRequest,
  UpdateTradeRequest,
  TradeFilters,
  TradeStats,
  ImportTradesResult,
  DetectPlatformResponse,
  ProfitCalendarResponse,
} from '../types/trade';

export interface GetTradesParams extends PaginationParams, SortParams, TradeFilters {}

export const tradesApi = {
  getTrades: (params: GetTradesParams): Promise<ApiResponse<PaginatedResponse<Trade>>> =>
    apiClient.get('/trades', { params }),

  getTradeById: (id: number): Promise<ApiResponse<Trade>> =>
    apiClient.get(`/trades/${id}`),

  createTrade: (data: CreateTradeRequest): Promise<ApiResponse<Trade>> =>
    apiClient.post('/trades', data),

  updateTrade: (id: number, data: UpdateTradeRequest): Promise<ApiResponse<Trade>> =>
    apiClient.put(`/trades/${id}`, data),

  deleteTrade: (id: number): Promise<ApiResponse<void>> =>
    apiClient.delete(`/trades/${id}`),

  closeTrade: (id: number, data: { exit_price: number; close_time: string; profit_loss: number }): Promise<ApiResponse<Trade>> =>
    apiClient.post(`/trades/${id}/close`, data),

  getTradeStats: (params?: { period?: string; start_date?: string; end_date?: string }): Promise<ApiResponse<TradeStats>> =>
    apiClient.get('/trades/stats', { params }),

  detectPlatform: (formData: FormData): Promise<ApiResponse<DetectPlatformResponse>> =>
    createFormDataRequest('/trades/detect-platform', formData),

  importTrades: (
    formData: FormData,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<ImportTradesResult>> =>
    createFormDataRequest('/trades/import', formData, onProgress),

  exportTrades: (params?: TradeFilters): Promise<Blob> =>
    apiClient.get('/trades/export', {
      params,
      responseType: 'blob',
    }),

  // 交易截图相关
  getTradeImages: (tradeId: number): Promise<ApiResponse<TradeImage[]>> =>
    apiClient.get(`/trades/${tradeId}/images`),

  uploadTradeImages: (
    tradeId: number,
    formData: FormData,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<TradeImage[]>> =>
    createFormDataRequest(`/trades/${tradeId}/images`, formData, onProgress),

  updateTradeImage: (
    tradeId: number,
    imageId: number,
    data: { image_type?: TradeImageType; caption?: string; sort_order?: number }
  ): Promise<ApiResponse<TradeImage>> =>
    apiClient.put(`/trades/${tradeId}/images/${imageId}`, data),

  deleteTradeImage: (tradeId: number, imageId: number): Promise<ApiResponse<void>> =>
    apiClient.delete(`/trades/${tradeId}/images/${imageId}`),

  // Profit Calendar
  getProfitCalendar: (params: { year: number; month: number }): Promise<ApiResponse<ProfitCalendarResponse>> =>
    apiClient.get('/trades/profit-calendar', { params }),
};
